import { isEmpty, keys, omit } from 'ramda';
import type { Location } from 'slate';
import { Editor, Transforms } from 'slate';

import type { FooterElement, FootersElement, SuggestedFootersEdit } from '../internal';
import { AlineTransforms, EditingMode, isFooterElement, Queries } from '../internal';

type FootersElementProps = Omit<FootersElement, 'children' | 'suggestedEdits' | 'type'>;
type FooterElementProps = Omit<FooterElement, 'children' | 'suggestedEdits' | 'type'>;

const FooterTransforms = {
  /**
   * Note: This API isn't used anywhere outside of tests.
   * ToDo: Make API work with suggesting/tracking changes before using in production..
   */
  setFooterProps(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props:
        | Partial<FooterElementProps>
        | ((oldProps: Omit<FooterElement, 'children'>) => Partial<FooterElementProps>);
      at?: Location;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, props } = options;
      if (!at) return;

      const footerEntries = Editor.nodes<FooterElement>(editor, { at, match: isFooterElement });

      for (const [footer, footerPath] of footerEntries) {
        const newProps = typeof props === 'function' ? props(omit(['children'], footer)) : props;

        Transforms.setNodes(editor, newProps, { at: footerPath });
      }
    });
  },
  setFootersElementProps(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props:
        | Partial<FootersElementProps>
        | ((oldProps: Omit<FootersElement, 'children'>) => Partial<FootersElementProps>);
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, props } = options;
      const footersEntry = Queries.getFootersElement(editor);
      if (!footersEntry) return;

      const [footers, footersPath] = footersEntry;
      const newProps = typeof props === 'function' ? props(omit(['children'], footers)) : props;
      const equalKeys = keys(newProps).filter(propKey => {
        switch (propKey) {
          case 'footerMargin': {
            const activeFooterMargin = Queries.renderedHeaderOrFooterMargin(footers, 'in');
            return activeFooterMargin === newProps.footerMargin;
          }
        }
      });
      const nonEqualProps = omit(equalKeys, newProps);

      if (isEmpty(nonEqualProps)) return;

      if (editingMode === EditingMode.SUGGESTING) {
        const footersEdit: SuggestedFootersEdit = {
          authorId,
          type: 'footerFormat',
          value: nonEqualProps,
        };

        AlineTransforms.suggestFootersEdit(editor, { editingMode, footersEdit });

        return;
      }

      AlineTransforms.removeConflictingSuggestions(editor, {
        editingMode,
        entry: footersEntry,
        props: nonEqualProps,
      });
      Transforms.setNodes(editor, newProps, { at: footersPath });
    });
  },
};

export default FooterTransforms;
